import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    ChevronLeft, Users, Mail, Send, Eye, MessageSquare, Clock,
    Sparkles, CheckCircle, X, ArrowRight, RefreshCw, Edit3, Activity, AlertTriangle, Ban
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
    scheduled: { icon: Clock, color: 'text-stone-500', bg: 'bg-stone-100', border: 'border-stone-200', label: 'Scheduled' },
    sent: { icon: Send, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Sent' },
    opened: { icon: Eye, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Opened' },
    responded: { icon: MessageSquare, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Responded' },
    bounced: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Bounced' },
    complained: { icon: Ban, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Complained' },
};

export default function SequenceMonitor() {
    const { sequenceId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [seq, setSeq] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [refreshing, setRefreshing] = useState(false);
    const [analytics, setAnalytics] = useState(null);

    const loadSequence = useCallback(async () => {
        try {
            const headers = getAuthHeader();
            const [seqRes, analyticsRes] = await Promise.all([
                axios.get(`${API}/sequences/${sequenceId}`, { headers }),
                axios.get(`${API}/sequences/analytics/${sequenceId}`, { headers }).catch(() => ({ data: null })),
            ]);
            setSeq(seqRes.data);
            setAnalytics(analyticsRes.data);
        } catch { toast.error('Failed to load sequence'); }
        finally { setLoading(false); setRefreshing(false); }
    }, [sequenceId]);

    useEffect(() => { loadSequence(); }, [loadSequence]);

    const handleRefresh = () => { setRefreshing(true); loadSequence(); };

    const handleAcceptSuggestion = async (scId, emailId, suggestionId) => {
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${scId}/emails/${emailId}/suggestions/${suggestionId}/accept`,
                {}, { headers: getAuthHeader() }
            );
            setSeq(res.data);
            toast.success('AI suggestion accepted — email updated');
        } catch { toast.error('Failed to accept suggestion'); }
    };

    const handleDismissSuggestion = async (scId, emailId, suggestionId) => {
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${scId}/emails/${emailId}/suggestions/${suggestionId}/dismiss`,
                {}, { headers: getAuthHeader() }
            );
            setSeq(res.data);
            toast.success('Suggestion dismissed');
        } catch { toast.error('Failed to dismiss suggestion'); }
    };

    const handleSendEmail = async (scId, emailId, contactName) => {
        if (!window.confirm(`Send this email to ${contactName}?`)) return;
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${scId}/emails/${emailId}/send`,
                {}, { headers: getAuthHeader() }
            );
            setSeq(res.data);
            toast.success(`Email sent to ${contactName}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;
    if (!seq) return null;

    // Build flat timeline of all emails
    const timeline = [];
    let pendingSuggestions = [];
    for (const contact of seq.contacts || []) {
        for (const email of contact.emails || []) {
            timeline.push({ ...email, contact_name: contact.contact_name, contact_title: contact.contact_title, sc_id: contact.sc_id });
            for (const sug of email.ai_suggestions || []) {
                if (sug.status === 'pending') {
                    pendingSuggestions.push({ ...sug, email, contact_name: contact.contact_name, sc_id: contact.sc_id, email_id: email.email_id });
                }
            }
        }
    }

    const filteredTimeline = filter === 'all' ? timeline : timeline.filter(e => e.status === filter);
    const stats = { scheduled: 0, sent: 0, opened: 0, responded: 0, bounced: 0, complained: 0 };
    timeline.forEach(e => { if (stats[e.status] !== undefined) stats[e.status]++; });

    const delivered = stats.sent + stats.opened + stats.responded;
    const openRate = delivered ? Math.round((stats.opened + stats.responded) / delivered * 100) : 0;
    const responseRate = delivered ? Math.round(stats.responded / delivered * 100) : 0;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/email-sequences')} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" data-testid="back-to-sequences">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="monitor-title">{seq.name}</h1>
                        <p className="text-xs text-stone-400">{seq.account_name} &middot; Monitoring &middot; {(seq.contacts || []).length} contacts &middot; {timeline.length} emails</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/email-sequences/${sequenceId}/edit`)} data-testid="edit-from-monitor">
                        <Edit3 className="w-4 h-4 mr-1.5" />Edit
                    </Button>
                </div>
            </div>

            {/* Analytics Header */}
            {analytics && timeline.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="monitor-analytics">
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Engagement Funnel */}
                        <div className="lg:col-span-3">
                            <p className="text-xs text-stone-400 font-semibold uppercase tracking-wider mb-3">Engagement Funnel</p>
                            <div className="space-y-2">
                                {(analytics.engagement_funnel || []).map((stage, i) => {
                                    const maxCount = Math.max(...(analytics.engagement_funnel || []).map(f => f.count), 1);
                                    const pct = Math.max(Math.round((stage.count / maxCount) * 100), stage.count > 0 ? 8 : 0);
                                    const colors = ['bg-stone-400', 'bg-blue-500', 'bg-emerald-500', 'bg-violet-500'];
                                    return (
                                        <div key={stage.stage} className="flex items-center gap-2" data-testid={`analytics-funnel-${stage.stage.toLowerCase()}`}>
                                            <span className="text-xs text-stone-500 w-16 text-right">{stage.stage}</span>
                                            <div className="flex-1 bg-stone-100 rounded-full h-5 overflow-hidden">
                                                <div className={`h-full ${colors[i]} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                                                    style={{ width: `${pct}%` }}>
                                                    {stage.count > 0 && <span className="text-xs text-white font-bold">{stage.count}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {/* Rate Cards */}
                        <div className="lg:col-span-2 grid grid-cols-2 gap-3">
                            <div className="bg-emerald-50 rounded-lg p-3 text-center">
                                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{openRate}%</p>
                                <p className="text-xs text-emerald-600">Open Rate</p>
                            </div>
                            <div className="bg-violet-50 rounded-lg p-3 text-center">
                                <p className="text-2xl font-bold text-violet-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{responseRate}%</p>
                                <p className="text-xs text-violet-600">Response Rate</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{delivered}</p>
                                <p className="text-xs text-blue-600">Delivered</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3 text-center">
                                <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.bounced}</p>
                                <p className="text-xs text-red-500">Bounced</p>
                            </div>
                        </div>
                    </div>

                    {/* Contact Performance Table */}
                    {(analytics.contact_stats || []).length > 1 && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <p className="text-xs text-stone-400 font-semibold uppercase tracking-wider mb-2">Contact Performance</p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-stone-400 border-b border-stone-100">
                                            <th className="text-left py-1.5 font-medium">Contact</th>
                                            <th className="text-center py-1.5 font-medium">Emails</th>
                                            <th className="text-center py-1.5 font-medium">Sent</th>
                                            <th className="text-center py-1.5 font-medium">Opened</th>
                                            <th className="text-center py-1.5 font-medium">Responded</th>
                                            <th className="text-center py-1.5 font-medium">Bounced</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analytics.contact_stats.map((cs, i) => (
                                            <tr key={i} className="border-b border-stone-50" data-testid={`contact-perf-${i}`}>
                                                <td className="py-1.5">
                                                    <span className="font-medium text-stone-700">{cs.contact_name}</span>
                                                    {cs.contact_title && <span className="text-stone-400 ml-1">({cs.contact_title})</span>}
                                                </td>
                                                <td className="text-center text-stone-600">{cs.emails}</td>
                                                <td className="text-center text-blue-600">{cs.sent}</td>
                                                <td className="text-center text-emerald-600">{cs.opened}</td>
                                                <td className="text-center text-violet-600">{cs.responded}</td>
                                                <td className="text-center text-red-500">{cs.bounced}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Stats Bar */}
            <div className="grid grid-cols-4 gap-3" data-testid="monitor-stats">
                {Object.entries(statusConfig).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                        <button key={key} onClick={() => setFilter(filter === key ? 'all' : key)}
                            className={`p-3 rounded-xl border transition-all text-left ${filter === key ? `${cfg.bg} ${cfg.border} ring-1 ring-offset-1` : 'bg-white border-stone-200 hover:border-stone-300'}`}
                            data-testid={`filter-${key}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-stone-400 font-medium">{cfg.label}</p>
                                    <p className={`text-2xl font-bold ${cfg.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{stats[key]}</p>
                                </div>
                                <Icon className={`w-5 h-5 ${cfg.color}`} />
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* AI Suggestions Banner */}
            {pendingSuggestions.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5" data-testid="ai-suggestions-banner">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-amber-900">Cross-Contact Intelligence</h3>
                            <p className="text-xs text-amber-700 mt-0.5">
                                {pendingSuggestions.length} AI rewrite suggestion{pendingSuggestions.length > 1 ? 's' : ''} based on engagement signals from contacts in this sequence.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {pendingSuggestions.map(sug => (
                            <div key={sug.suggestion_id} className="bg-white rounded-lg border border-amber-200 p-4" data-testid={`suggestion-${sug.suggestion_id}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">AI Rewrite</span>
                                            <span className="text-xs text-stone-500">for {sug.contact_name}</span>
                                            <ArrowRight className="w-3 h-3 text-stone-400" />
                                            <span className="text-xs text-stone-500">Email #{sug.email.email_number}</span>
                                        </div>
                                        <p className="text-xs text-amber-600 mt-1">{sug.reasoning}</p>
                                    </div>
                                </div>

                                {/* Before/After comparison */}
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div className="bg-stone-50 rounded-md p-3 border border-stone-200">
                                        <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">Original</p>
                                        <p className="text-xs font-medium text-stone-700">{sug.original_subject || '(No subject)'}</p>
                                        <p className="text-xs text-stone-500 mt-1 line-clamp-3 whitespace-pre-wrap">{sug.original_body || '(No body)'}</p>
                                    </div>
                                    <div className="bg-amber-50 rounded-md p-3 border border-amber-200">
                                        <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1.5">Suggested</p>
                                        <p className="text-xs font-medium text-stone-700">{sug.suggested_subject}</p>
                                        <p className="text-xs text-stone-600 mt-1 line-clamp-3 whitespace-pre-wrap">{sug.suggested_body}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                                        onClick={() => handleAcceptSuggestion(sug.sc_id, sug.email_id, sug.suggestion_id)}
                                        data-testid={`accept-${sug.suggestion_id}`}>
                                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Accept Rewrite
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-stone-500 hover:text-stone-700"
                                        onClick={() => handleDismissSuggestion(sug.sc_id, sug.email_id, sug.suggestion_id)}
                                        data-testid={`dismiss-${sug.suggestion_id}`}>
                                        <X className="w-3.5 h-3.5 mr-1.5" />Dismiss
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Timeline */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-stone-700">
                        Email Timeline {filter !== 'all' && <span className="text-stone-700 capitalize">— {filter}</span>}
                    </h2>
                    {filter !== 'all' && (
                        <button onClick={() => setFilter('all')} className="text-xs text-stone-700 hover:underline" data-testid="clear-filter">Show All</button>
                    )}
                </div>

                {filteredTimeline.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                        <Activity className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                        <p className="text-stone-500">No emails match the current filter</p>
                    </div>
                ) : (
                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-6 top-0 bottom-0 w-px bg-stone-200" />

                        <div className="space-y-4">
                            {filteredTimeline.map((item, idx) => {
                                const cfg = statusConfig[item.status] || statusConfig.scheduled;
                                const Icon = cfg.icon;
                                const hasPendingSug = (item.ai_suggestions || []).some(s => s.status === 'pending');
                                const acceptedSugs = (item.ai_suggestions || []).filter(s => s.status === 'accepted');
                                return (
                                    <div key={item.email_id} className="relative flex gap-4 pl-2" data-testid={`timeline-item-${idx}`}>
                                        {/* Timeline dot */}
                                        <div className={`w-8 h-8 rounded-full ${cfg.bg} border-2 ${cfg.border} flex items-center justify-center flex-shrink-0 z-10 bg-white`}>
                                            <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                                        </div>

                                        {/* Content card */}
                                        <div className={`flex-1 bg-white rounded-lg border ${hasPendingSug ? 'border-amber-300 shadow-amber-100 shadow-sm' : 'border-stone-200'} p-4 hover:shadow-sm transition-shadow`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-xs font-bold text-stone-700">#{item.email_number}</span>
                                                        <span className="text-xs text-stone-400">&rarr;</span>
                                                        <span className="text-sm font-medium text-stone-800">{item.contact_name}</span>
                                                        {item.contact_title && <span className="text-xs text-stone-400">({item.contact_title})</span>}
                                                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${cfg.bg} ${cfg.color}`}>
                                                            {item.status}
                                                        </span>
                                                        {hasPendingSug && (
                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex items-center gap-1">
                                                                <Sparkles className="w-3 h-3" />AI Rewrite
                                                            </span>
                                                        )}
                                                        {acceptedSugs.length > 0 && (
                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex items-center gap-1">
                                                                <CheckCircle className="w-3 h-3" />AI Enhanced
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-stone-700 mt-1 font-medium truncate">{item.subject || '(No subject)'}</p>
                                                    {item.body && <p className="text-xs text-stone-400 mt-0.5 line-clamp-2">{item.body}</p>}
                                                </div>
                                                <div className="text-right flex-shrink-0 ml-4 flex flex-col items-end gap-1.5">
                                                    {item.scheduled_date && (
                                                        <p className="text-xs text-stone-400 flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />{item.scheduled_date}
                                                        </p>
                                                    )}
                                                    {item.status === 'scheduled' && (
                                                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                                            onClick={() => handleSendEmail(item.sc_id, item.email_id, item.contact_name)}
                                                            data-testid={`send-timeline-${idx}`}>
                                                            <Send className="w-3 h-3 mr-1" />Send
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Engagement events */}
                                            {(item.engagement_events || []).length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-stone-100">
                                                    <div className="flex flex-wrap gap-2">
                                                        {item.engagement_events.map((ev, i) => (
                                                            <span key={i} className="text-xs text-stone-500 flex items-center gap-1 bg-stone-50 px-2 py-1 rounded-md">
                                                                <span className={`w-1.5 h-1.5 rounded-full ${ev.event_type === 'responded' ? 'bg-violet-500' : ev.event_type === 'opened' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                                                                <span className="capitalize">{ev.event_type}</span>
                                                                <span className="text-stone-400">{new Date(ev.timestamp).toLocaleString()}</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
